import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { ItemCarrinho, ProdutoCardapio } from '../../delivery.types';
import { useDeliveryStore } from '../../store/deliveryStore';
import { fmtMoeda } from '../../utils';
import DialogProduto from '../DialogProduto';

export default function StepCarrinho() {
  const {
    carrinho,
    unidade,
    tipoRetirada,
    removerItem,
    atualizarQuantidade,
    setStep,
    totalCarrinho,
    totalComTaxa,
  } = useDeliveryStore();

  const [editando, setEditando] = useState<{ item: ItemCarrinho; produto: ProdutoCardapio } | null>(null);

  const taxa = tipoRetirada === 'ENTREGA' ? (unidade?.taxaEntrega ?? 0) : 0;

  function encontrarProduto(codigoProduto: number): ProdutoCardapio | undefined {
    for (const cat of unidade ? [] : []) void cat; // apenas inicialização
    const catalogo = useDeliveryStore.getState().catalogo;
    for (const cat of catalogo) {
      const p = cat.produtos.find(p => p.codigoProduto === codigoProduto);
      if (p) return p;
    }
    return undefined;
  }

  function abrirEdicao(item: ItemCarrinho) {
    const produto = encontrarProduto(item.codigoProduto);
    if (produto) setEditando({ item, produto });
  }

  const ehPersonalizavel = (item: ItemCarrinho) =>
    item.complementos.length > 0 || item.grupoSelecoes.length > 0;

  if (carrinho.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <Typography color="text.secondary" mb={2}>
          Seu carrinho está vazio.
        </Typography>
        <Button variant="outlined" onClick={() => setStep(1)}>
          Ver cardápio
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box>
        {carrinho.map((item) => {
          const extrasCompl = item.complementos
            .filter((c) => c.tipo === 'ADICIONAR')
            .reduce((s, c) => s + c.valorUnitario * c.quantidade, 0);
          const extrasGrupos = (item.grupoSelecoes ?? []).reduce((s, g) => {
            if (!g.itens.length) return s;
            if (g.estrategiaPreco === 'MAIOR_VALOR') {
              return s + Math.max(...g.itens.map((i) => i.valorAdicional));
            }
            return s + g.itens.reduce((gs, i) => gs + i.valorAdicional, 0);
          }, 0);
          const precoUnit = item.valorUnitario + extrasCompl + extrasGrupos;
          const personalizavel = ehPersonalizavel(item);

          return (
            <React.Fragment key={item.id}>
              <Box sx={{ py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {/* Qtd */}
                <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      item.quantidade > 1
                        ? atualizarQuantidade(item.id, item.quantidade - 1)
                        : removerItem(item.id)
                    }
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography fontWeight={600} minWidth={18} textAlign="center">
                    {item.quantidade}
                  </Typography>
                  <IconButton size="small" onClick={() => atualizarQuantidade(item.id, item.quantidade + 1)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Descrição + detalhes */}
                <Box flex={1} minWidth={0}>
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {item.descricaoProduto}
                  </Typography>
                  {(item.grupoSelecoes ?? []).map((g) => {
                    const precoGrupo = g.itens.some((i) => i.valorAdicional > 0)
                      ? g.estrategiaPreco === 'MAIOR_VALOR'
                        ? Math.max(...g.itens.map((i) => i.valorAdicional))
                        : g.itens.reduce((s, i) => s + i.valorAdicional, 0)
                      : 0;
                    return (
                      <Typography key={g.codigoGrupo} variant="caption" color="text.secondary" display="block">
                        {g.tituloGrupo}: {g.itens.map((i) => i.descricao).join(', ')}
                        {precoGrupo > 0 ? ` (+${fmtMoeda(precoGrupo)})` : ''}
                      </Typography>
                    );
                  })}
                  {item.complementos.map((c, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" display="block">
                      {c.tipo === 'REMOVER' ? `- Sem ${c.descricaoProduto}` : `+ ${c.descricaoProduto}`}
                      {c.tipo === 'ADICIONAR' && c.valorUnitario > 0
                        ? ` (${fmtMoeda(c.valorUnitario)})`
                        : ''}
                    </Typography>
                  ))}
                  {item.observacao && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Obs: {item.observacao}
                    </Typography>
                  )}
                </Box>

                {/* Preço + ações */}
                <Box display="flex" flexDirection="column" alignItems="flex-end" flexShrink={0}>
                  <Typography variant="body2" fontWeight={700}>
                    {fmtMoeda(precoUnit * item.quantidade)}
                  </Typography>
                  <Box display="flex">
                    {personalizavel && (
                      <IconButton size="small" onClick={() => abrirEdicao(item)} sx={{ color: 'primary.main' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => removerItem(item.id)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
              <Divider />
            </React.Fragment>
          );
        })}
      </Box>

      <Box mt={2}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography color="text.secondary">Subtotal</Typography>
          <Typography>{fmtMoeda(totalCarrinho())}</Typography>
        </Box>
        {taxa > 0 && (
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography color="text.secondary">Taxa de entrega</Typography>
            <Typography>{fmtMoeda(taxa)}</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography fontWeight={700}>Total</Typography>
          <Typography fontWeight={700} color="primary">
            {fmtMoeda(totalComTaxa())}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" gap={2} mt={3}>
        <Button variant="outlined" fullWidth onClick={() => setStep(1)}>
          Adicionar mais
        </Button>
        <Button variant="contained" fullWidth onClick={() => setStep(3)}>
          Finalizar pedido
        </Button>
      </Box>

      {editando && (
        <DialogProduto
          produto={editando.produto}
          itemEditando={editando.item}
          onClose={() => setEditando(null)}
        />
      )}
    </Box>
  );
}
